import { useFormik } from "formik";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { signUpWithInvitation, signUpWithPassword, signUpWithRTS } from "../../behavior/api/auth";
import { storeTokens } from "../../behavior/security/auth.service";
import type { LargeAlertProps } from "../../components/elements/LargeAlert";
import Link from "../../components/elements/Link";
import type { TPageState } from "../../constants";
import { PageRoute, PageState } from "../../constants";
import type { Invitation } from "../../types/invitation";
import { registrationInviteEmailFormSchema } from "../../utils/validationSchemas";
import { Button, Text } from "../elements";
import { Password, Textbox } from "../forms";

interface RegistrationInviteEmailProps {
    setShowAlert: Dispatch<SetStateAction<LargeAlertProps>>;
    invitation?: Invitation;
    state: TPageState;
}

const RegistrationInviteEmailForm = ({ setShowAlert, invitation, state }: RegistrationInviteEmailProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);

    const normalSignUpButton = useMemo(() => {
        return state === PageState.INTERVIEW_COMPLETED || state === PageState.JOB_APPLY_POST_INTERVIEW_SIGN_UP;
    }, [state]);

    const signUpWithInvitationToken = async (email: string, password: string, token: string) => {
        try {
            const response = await signUpWithInvitation({
                email,
                password,
                token,
                language: i18n.language,
            });

            if (!response) {
                toast.error(t("AppMessages.server-error"));
                return;
            }
            if (response.isError && response.errorMessage) {
                toast.error(t(`AppMessages.${response.errorMessage}`));
            } else if (response.payload?.access_token) {
                storeTokens(response.payload);
                if (state === PageState.SIGN_UP || state === PageState.INTERVIEW_COMPLETED) {
                    navigate(PageRoute.DASHBOARD, {
                        replace: true,
                    });
                    return;
                }
                if (!invitation) return;

                const urlSearchParams = new URLSearchParams({
                    interviewId: invitation.publicInterviewId ?? invitation.interviewId,
                    token: response.payload.access_token,
                    displayName: invitation.invitee.displayName ?? invitation.invitee.email,
                });
                navigate(`${PageRoute.PRE_INTERVIEW}?${urlSearchParams}`, {
                    replace: true,
                });
            }
        } catch (e) {
            toast.success(t((e as Error).message));
        }
    };

    const signUpWithRTSToken = async (email: string, password: string, token: string) => {
        try {
            const response = await signUpWithRTS({
                email,
                password,
                token,
                language: i18n.language,
            });

            if (!response) {
                toast.error(t("AppMessages.server-error"));
                return;
            }
            if (response.isError && response.errorMessage) {
                toast.error(t(`AppMessages.${response.errorMessage}`));
            } else if (response.payload?.access_token) {
                storeTokens(response.payload);
                navigate(`${PageRoute.DASHBOARD}?state=${PageState.INTERVIEW_COMPLETED}`);
            }
        } catch (e) {
            toast.success(t((e as Error).message));
        }
    };

    const signUp = async (email: string, password: string) => {
        try {
            const response = await signUpWithPassword({
                firstName: "",
                lastName: "",
                email,
                password,
                phoneNumber: "",
                countryCode: "",
                language: i18n.language,
            });

            if (response.payload) {
                setShowAlert({
                    title: t("AppMessages.verify-email-title"),
                    subtitle: t("AppMessages.verify-email-subtitle"),
                    type: "info",
                });
            } else if (response.errorMessage === "email-is-taken") {
                toast.error(t("Registration.Error_Email_Exists"));
                return;
            } else if (response.errorMessage) {
                toast.error(t(`AppMessages.${response.errorMessage}`));
            }
        } catch (e) {
            toast.error(t((e as Error).message));
        }
    };

    const { values, errors, touched, handleSubmit, handleChange, handleBlur, setFieldValue } = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: registrationInviteEmailFormSchema,
        onSubmit: async (values) => {
            if (values) {
                setLoading(true);
                try {
                    const token = searchParams.get("token");
                    if (token) {
                        if (state === PageState.JOB_APPLY_POST_INTERVIEW_SIGN_UP) {
                            return await signUpWithRTSToken(values.email, values.password, token);
                        } else {
                            return await signUpWithInvitationToken(values.email, values.password, token);
                        }
                    } else {
                        return await signUp(values.email, values.password);
                    }
                } finally {
                    setLoading(false);
                }
            }
        },
    });

    useEffect(() => {
        const email = invitation?.invitee.email ?? searchParams.get("email");

        if (email) {
            setFieldValue("email", email);
        }
    }, [invitation, searchParams, setFieldValue]);

    const emailPasswordSet = values.email && values.password && !errors.email && !errors.password;
    const canSubmit = emailPasswordSet && !loading;

    return (
        <form className="registration-invite-email-form" onSubmit={handleSubmit}>
            <Textbox
                id="email"
                label={`${t("Registration.Label_Email")}*`}
                name="email"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.email}
                error={errors.email}
                touched={touched.email}
            />
            <Password
                id="password"
                label={`${t("Registration.Label_Password")}*`}
                name="password"
                helpText={t("Registration.HelpText_Password")}
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.password}
                error={errors.password}
                touched={touched.password}
            />
            <div className="terms-and-condition-text-wrapper">
                <Text size="small">{`${t("RegistrationInviteEmail.PrivacyPolicyText")} `}</Text>
                <Link to={"https://www.hubert.ai/candidate-privacy-policy"} target="_blank" rel="noreferrer">
                    <Text size="small" weight="semibold">
                        {t("RegistrationInviteEmail.PrivacyPolicyLink")}
                    </Text>
                    .
                </Link>
            </div>
            <Button fullWidth submit className="mt-2" disabled={!canSubmit}>
                {normalSignUpButton
                    ? t("RegistrationInviteEmail.ButtonText_SignUpNormal")
                    : t("RegistrationInviteEmail.ButtonText_SignUp")}
            </Button>
        </form>
    );
};

export default RegistrationInviteEmailForm;
